import React, { useRef, useState } from 'react'
import {
  Input,
  Notification,
  NotificationType,
  Content,
  Button
} from '@ampcore/adel-react'
import {
  isValidEmail,
  formatPhoneNumber,
  isValidPhoneNumber
} from '../../../common/utils'
import { formSelectedData, CONSTs } from '../views/utils'
import '../views/index.css'
import { Spacer } from '@ampcore/design-system'
import xss from 'xss'

export type FormFieldName = 'firstname' | 'lastname' | 'emailaddress' | 'phone'

export type FormDataState = {
  firstname: string | null
  lastname: string | null
  emailaddress: string | null
  phone: string | null
}

export type ErrorFieldsState = {
  firstname: boolean
  lastname: boolean
  emailaddress: boolean
  phone: boolean
  emailWrongFormat: boolean
}

export type FormLabels = {
  firstname: string
  lastname: string
  emailaddress: string
  phone: string
  buttonlabel: string
  buttonLoadingLabel: string
}

export type FormContent = {
  title: string
  introBody: string
  disclosure: string
}

export type FormEmailInfo = {
  emailApi: string
  emailFormHeading: string
  emailGreeting: string
  emailIntro: string
  subject: string
  toEmailAddress: string
}

export type FormErrorMessages = {
  errorFirstname: string
  errorLastname: string
  errorEmailaddress: string
  errorEmailaddressWrongFormat: string
  errorPhone: string
}

export type ServiceMessages = {
  serviceSuccessThankyou: string
  serviceSuccess: string
  serviceFailed: string
}

export type PleaseNote = {
  message?: string
}

export type ContactFormAPRIContent = {
  componentSpacing?: React.ComponentProps<typeof Spacer>['spacing']
  formLabels: FormLabels
  formContent: FormContent
  formEmailInfo?: FormEmailInfo
  formErrorMessages: FormErrorMessages
  serviceMessages?: Partial<ServiceMessages>
  formPleaseNote?: PleaseNote
  formTemplate?: string
}

export type ShowServerResponse = (props: unknown, message?: string) => void

export type ContactFormAPRISubmitCallback = (
  items: unknown,
  updatedEmailInfo: FormEmailInfo,
  showServerResponse: ShowServerResponse,
  showEmailIntroMessage: boolean
) => void

export type ContactFormAPRIProps = {
  callback?: ContactFormAPRISubmitCallback
  name?: string
  content: ContactFormAPRIContent
  testId?: string
}

const ContactFormAPRI = (props: ContactFormAPRIProps) => {
  const { callback, name, content } = props
  const [formData, setFormData] = useState<FormDataState>({
    firstname: null,
    lastname: null,
    emailaddress: null,
    phone: null
  })

  const [errorFields, setErrorFields] = useState<ErrorFieldsState>({
    firstname: false,
    lastname: false,
    emailaddress: false,
    phone: false,
    emailWrongFormat: false
  })

  const phoneRef = useRef<HTMLInputElement | null>(null)
  const [loading, setLoading] = useState(false)
  const [showSuccessMessage, setShowSuccessMessage] = useState(false)
  const [showServiceFailedMessage, setShowServiceFailedMessage] =
    useState(false)
  const successMessage = (
    <>
      <h3 className="u-paddingBottom16">
        {content?.serviceMessages?.serviceSuccessThankyou}
      </h3>
      <p
        className="u-paddingTop16 u-paddingBottom16"
        dangerouslySetInnerHTML={{
          __html: xss(content?.serviceMessages?.serviceSuccess ?? '')
        }}
      />
    </>
  )

  const validateField = (fieldName: FormFieldName, value: string) => {
    const fieldErrors: ErrorFieldsState = { ...errorFields }
    const updatedFormData: FormDataState = { ...formData }
    const trimmedValue = value.trim()
    const isNotEmpty = trimmedValue.length > 0

    switch (fieldName) {
    case 'firstname': {
      fieldErrors.firstname = !isNotEmpty
      updatedFormData.firstname = trimmedValue || null
      break
    }
    case 'lastname': {
      fieldErrors.lastname = !isNotEmpty
      updatedFormData.lastname = trimmedValue || null
      break
    }
    case 'emailaddress': {
      const isEmailValid = isValidEmail(value)
      fieldErrors.emailaddress = !isEmailValid
      fieldErrors.emailWrongFormat = !isEmailValid && isNotEmpty
      updatedFormData.emailaddress = fieldErrors.emailaddress ? null : value
      break
    }
    case 'phone': {
      const isValidPhone = isValidPhoneNumber(value, true)
      fieldErrors.phone = !isValidPhone

      if (isValidPhone) {
        const formattedPhone = formatPhoneNumber(value)

        if (phoneRef.current) {
          phoneRef.current.value = formattedPhone
        }

        updatedFormData.phone = formattedPhone
      } else {
        updatedFormData.phone = null
      }
      break
    }
    }

    setFormData(updatedFormData)
    setErrorFields(fieldErrors)
  }

  const handleFieldChange = (event: React.SyntheticEvent<HTMLElement>) => {
    const target = event.target as HTMLInputElement | null

    if (!target || !target.name) {
      return
    }

    validateField(target.name as FormFieldName, target.value || '')
  }

  const isFormComplete = Object.entries(formData).every(([key, value]) => {
    return key === 'phone' || value !== null
  })

  const submitForm = (event: { preventDefault: () => void }) => {
    event.preventDefault()

    if (!isFormComplete) {
      const fieldErrors: ErrorFieldsState = { ...errorFields }
      fieldErrors.firstname = !formData.firstname
      fieldErrors.lastname = !formData.lastname
      fieldErrors.emailaddress = !isValidEmail(formData.emailaddress || '')
      fieldErrors.phone = !isValidPhoneNumber(formData.phone, true)
      setErrorFields(fieldErrors)
      return
    }

    setLoading(true)
    const items = formSelectedData(formData, content.formTemplate)

    if (
      callback &&
      typeof callback === 'function' &&
      items &&
      content.formEmailInfo
    ) {
      const showEmailIntroMessage = true
      const { emailGreeting, emailIntro, emailFormHeading } =
        content.formEmailInfo
      const updatedEmailInfo: FormEmailInfo = {
        ...content.formEmailInfo,
        emailIntro: `${emailGreeting},<br>${emailIntro}<br><br><b>${emailFormHeading}</b><br>`
      }

      callback(
        items,
        updatedEmailInfo,
        showServerResponse,
        showEmailIntroMessage
      )
    }
  }

  const showServerResponse: ShowServerResponse = (props, message) => {
    if (props && message) {
      if (message === CONSTs.SUCCESS) {
        setShowSuccessMessage(true)
        setShowServiceFailedMessage(false)
      } else {
        setShowSuccessMessage(false)
        setShowServiceFailedMessage(true)
      }

      setLoading(false)
    }
  }

  const renderSubmitButton = () => {
    const { formLabels } = content

    return (
      <Button
        onClick={submitForm}
        isLoading={loading}
        data-analytics={'Button'}
        data-tracking={'Button'}
        children={
          loading ? formLabels.buttonLoadingLabel : formLabels.buttonlabel
        }
      />
    )
  }

  const setEmailErrorMessage = (
    wrongEmail: boolean,
    message: FormErrorMessages
  ) => {
    return wrongEmail
      ? message.errorEmailaddressWrongFormat
      : message.errorEmailaddress
  }

  return (
    <Spacer spacing={content.componentSpacing}>
      {showSuccessMessage &&
        content.serviceMessages &&
        content.serviceMessages.serviceSuccess && (
        <Notification
          type={NotificationType.CONFIRM}
          children={successMessage}
          dismissable={false}
          className="FormNotificationAPRI u-sizeConstrained"
        />
      )}
      {!showSuccessMessage && (
        <div className="u-sizeConstrained">
          <div className="u-lg-size12of12 u-sm-sizeFull u-paddingBottom8">
            <div className="u-bgColorLight ContactFormAPRI-radius25">
              <div className="u-paddingVert64 u-lg-before1of12 u-lg-after1of12 u-sm-paddingHoriz40 u-md-paddingHoriz48">
                <h3 className="u-textDefault u-textCenter u-textSecondary u-textBold u-paddingBottom24">
                  {content.formContent.title}
                </h3>
                <h4 className="u-textDefault u-textCenter u-textColorBrand u-textSecondary u-textNormal u-lg-textSizeMsrp2 u-md-textSizeMsrp2 u-sm-textSizeMsrp1 u-paddingBottom8">
                  {content.formContent.introBody}
                </h4>
                <div>
                  <div
                    data-name={name}
                    onBlur={handleFieldChange}
                    onKeyUp={handleFieldChange}
                  >
                    <hr />
                    <div className="u-paddingTop24">
                      <div className="u-flex u-flexRow u-sm-flexCol">
                        <div className="u-size6of12 u-sm-sizeFull">
                          <Input
                            name="firstname"
                            id="firstname"
                            label={content.formLabels.firstname}
                            error={errorFields.firstname}
                            message={content.formErrorMessages.errorFirstname}
                            maxLength={100}
                          />
                        </div>
                        <div className="u-size6of12 u-lg-before1of12 u-md-before1of12 u-sm-sizeFull">
                          <Input
                            name="lastname"
                            id="lastname"
                            label={content.formLabels.lastname}
                            error={errorFields.lastname}
                            message={content.formErrorMessages.errorLastname}
                            maxLength={100}
                          />
                        </div>
                      </div>
                      <div className="u-flex u-flexRow u-sm-flexCol">
                        <div className="u-size6of12 u-sm-sizeFull">
                          <Input
                            name="emailaddress"
                            id="email"
                            label={content.formLabels.emailaddress}
                            error={errorFields.emailaddress}
                            message={setEmailErrorMessage(
                              errorFields.emailWrongFormat,
                              content.formErrorMessages
                            )}
                          />
                        </div>
                        <div className="u-size6of12 u-lg-before1of12 u-md-before1of12 u-sm-sizeFull">
                          <Input
                            name="phone"
                            id="phone"
                            label={content.formLabels.phone}
                            reference={phoneRef}
                            error={errorFields.phone}
                            message={content.formErrorMessages.errorPhone}
                          />
                        </div>
                      </div>
                      <div
                        className="Disclaimer-text u-paddingVert32"
                        dangerouslySetInnerHTML={{
                          __html: xss(content.formContent.disclosure)
                        }}
                      ></div>
                    </div>
                    {showServiceFailedMessage &&
                      content.serviceMessages?.serviceFailed && (
                      <Notification
                        type={NotificationType.ALERT}
                        message={content.serviceMessages.serviceFailed}
                        dismissable={false}
                        className="u-marginBottom24"
                      />
                    )}
                    <div className="u-sizeFull u-flex u-flexJustifyEnd">
                      {renderSubmitButton()}
                    </div>
                  </div>
                </div>
                {content.formPleaseNote?.message && (
                  <Content className={'u-marginBottom24'}>
                    <div
                      className="Content"
                      dangerouslySetInnerHTML={{
                        __html: xss(content.formPleaseNote.message)
                      }}
                    />
                  </Content>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </Spacer>
  )
}

export default ContactFormAPRI
